import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import Avatar from "@/components/Avatar";

// A player's picture with their name and Elo underneath, for the game header.
// Tapping it opens an enlarged view; tapping outside closes it. With
// `canViewProfile` (a person, in a game that can be left and picked up
// later) the enlarged view has a "View profile" button to /player/:id.
export default function PlayerChip({ userId, username, version, elo, isBot, label, fallbackClassName, testId, canViewProfile }) {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const showProfile = canViewProfile && !isBot && userId && userId !== "bot";
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="flex flex-col items-center gap-0.5 min-w-0 max-w-[4.5rem] md:max-w-[7rem] rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-brand"
        title={`${username} — tap to enlarge`}
        data-testid={testId}
      >
        <Avatar
          userId={isBot ? null : userId}
          username={username}
          version={version}
          bot={isBot}
          className="w-9 h-9 text-sm"
          fallbackClassName={fallbackClassName}
        />
        <span className="font-display font-bold text-[10px] md:text-[11px] leading-none truncate max-w-full">
          {username}
        </span>
        {elo ? (
          <span className="text-[9px] md:text-[10px] leading-none text-muted-foreground tabular-nums">
            {elo}
          </span>
        ) : null}
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-[18rem] rounded-2xl flex flex-col items-center text-center gap-3 p-8">
          <Avatar
            userId={isBot ? null : userId}
            username={username}
            version={version}
            bot={isBot}
            className="w-40 h-40 text-6xl font-display font-black"
            fallbackClassName={fallbackClassName}
          />
          <DialogTitle className="font-display text-2xl font-black tracking-tight break-all">
            {username}
          </DialogTitle>
          <DialogDescription className="text-sm text-muted-foreground">
            {label}{isBot ? " · Bot" : ""}{elo ? ` · ${elo} ELO` : ""}
          </DialogDescription>
          {showProfile && (
            <button
              type="button"
              onClick={() => { setOpen(false); navigate(`/player/${userId}`); }}
              className="mt-1 rounded-full border border-border px-4 py-1.5 text-sm font-semibold hover:bg-surface focus:outline-none focus-visible:ring-2 focus-visible:ring-brand"
              data-testid={`${testId}-profile`}
            >
              View profile
            </button>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
